import { Link } from '@tanstack/react-router';
import { ArrowUpRight } from 'lucide-react';

import { StudioLayout } from '@/shared/layout/studio-layout';
import { Button } from '@/shared/ui/button';
import { EmptyState } from '@/shared/ui/empty-state';
import { LoadingStatus } from '@/shared/ui/spinner';

import { useEarlierWorkshops } from '../api/use-earlier-workshops';

export function EarlierWorkshopsPage() {
  const earlier = useEarlierWorkshops();
  const items = earlier.data?.pages.flatMap((page) => page.items) ?? [];

  return (
    <StudioLayout activeId={null} subtitle="Earlier workshops">
      {() => (
        <div className="mx-auto w-full max-w-3xl px-5 py-6 sm:px-8 sm:py-8">
          <header className="mb-6">
            <h1 className="text-fg text-xl font-semibold tracking-tight">Earlier workshops</h1>
            <p className="text-fg-muted mt-2 text-sm leading-6">
              Saved workshops from earlier curriculum versions, with their original instructions,
              files and reviews. Browse the curriculum for your current lessons and workshops.
            </p>
          </header>

          {earlier.isPending ? <LoadingStatus label="Loading earlier workshops…" /> : null}

          {items.length > 0 ? (
            <ul aria-label="Saved earlier workshops" className="divide-line divide-y">
              {items.map((item) => (
                <li key={item.activityId}>
                  <Link
                    to="/workshop/$activityId"
                    params={{ activityId: item.activityId }}
                    className="text-fg hover:bg-surface focus-visible:ring-ring flex items-center gap-3 rounded-md px-3 py-4 text-sm outline-none focus-visible:ring-2"
                  >
                    <span className="min-w-0 flex-1">
                      <span className="text-fg-muted mb-1 block text-xs">{item.code}</span>
                      <span className="block break-words">{item.title}</span>
                    </span>
                    <span className="text-fg-muted shrink-0 text-xs">
                      {item.status === 'complete'
                        ? 'Complete'
                        : item.status === 'submitted'
                          ? 'In review'
                          : 'In progress'}
                    </span>
                    <ArrowUpRight className="text-fg-muted size-4 shrink-0" aria-hidden="true" />
                  </Link>
                </li>
              ))}
            </ul>
          ) : !earlier.isPending && !earlier.error ? (
            <EmptyState
              title="No earlier workshops"
              description="You have no saved workshops from earlier curriculum versions. Your current lessons and workshops are in the curriculum."
            />
          ) : null}

          {earlier.error ? (
            <div className="mt-4 text-sm" role="alert">
              <p>Could not load earlier workshops. Your saved work is still preserved.</p>
              <Button
                variant="secondary"
                size="sm"
                className="mt-2"
                loading={earlier.isFetching}
                onClick={() => void earlier.refetch()}
              >
                Try again
              </Button>
            </div>
          ) : null}

          {earlier.hasNextPage ? (
            <Button
              variant="secondary"
              size="sm"
              className="mt-4"
              loading={earlier.isFetchingNextPage}
              onClick={() => void earlier.fetchNextPage()}
            >
              Show more earlier workshops
            </Button>
          ) : null}
        </div>
      )}
    </StudioLayout>
  );
}
